import ChipInput from "@/components/base/ChipInput";
import { useTranslation } from "@/hooks/useTranslation";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Label,
	RadioGroup,
	RadioGroupItem,
	Typography,
} from "@st-marketplace/ui";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import type * as z from "zod";

export { addOrderAccountSchema } from "./schema";
import { addOrderAccountSchema } from "./schema";

export type AddOrderAccountData = z.infer<typeof addOrderAccountSchema>;

interface AddOrderFormProps {
	loading: boolean;
	onSubmit: (data: AddOrderAccountData) => void;
	formId: string;
	initialValues: AddOrderAccountData;
}

const AddOrderForm = ({
	loading,
	onSubmit,
	formId,
	initialValues,
}: AddOrderFormProps) => {
	const { t } = useTranslation();
	const credentialFieldId = useId();
	const [emailEditable, setEmailEditable] = useState(false);
	const [passwordEditable, setPasswordEditable] = useState(false);
	const [passwordVisible, setPasswordVisible] = useState(false);
	const form = useForm<AddOrderAccountData>({
		resolver: zodResolver(addOrderAccountSchema),
		defaultValues: initialValues,
	});
	const authType = form.watch("authType");

	return (
		<Form {...form}>
			<form
				id={formId}
				autoComplete="off"
				noValidate
				onSubmit={form.handleSubmit(onSubmit)}
				className="space-y-4"
			>
				<fieldset disabled={loading} className="min-w-0 space-y-4">
					<div className="space-y-4">
						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>{t("addOrder.inputEmail")}</FormLabel>
									<FormControl>
										<Input
											{...field}
											name={`ea-account-${credentialFieldId}`}
											ltr
											type="text"
											inputMode="email"
											autoComplete="off"
											autoCapitalize="none"
											autoCorrect="off"
											spellCheck={false}
											readOnly={!emailEditable}
											onPointerDown={() => setEmailEditable(true)}
											onFocus={() => setEmailEditable(true)}
											data-1p-ignore
											data-lpignore="true"
											data-bwignore
											placeholder="name@example.com"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="password"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>{t("addOrder.inputPassword")}</FormLabel>
									<FormControl>
										<Input
											{...field}
											name={`ea-password-${credentialFieldId}`}
											ltr
											type={passwordVisible ? "text" : "password"}
											autoComplete="off"
											readOnly={!passwordEditable}
											onPointerDown={() => setPasswordEditable(true)}
											onFocus={() => setPasswordEditable(true)}
											data-1p-ignore
											data-lpignore="true"
											data-bwignore
											append={
												<Button
													type="button"
													icon
													variant="text"
													color="ghost"
													size="sm"
													aria-label={
														passwordVisible ? "Hide password" : "Show password"
													}
													onClick={() =>
														setPasswordVisible((visible) => !visible)
													}
												>
													<Icon
														name={passwordVisible ? "eye-off-line" : "eye-line"}
														size="sm"
													/>
												</Button>
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<fieldset className="min-w-0 space-y-3 rounded-lg border border-secondary-border p-4">
						<legend className="px-1 font-medium">
							{t("addOrder.authMethod")}
						</legend>
						<RadioGroup
							value={authType}
							onValueChange={(value) =>
								form.setValue(
									"authType",
									value as AddOrderAccountData["authType"],
									{ shouldValidate: true },
								)
							}
							className="flex flex-wrap gap-6"
						>
							<div className="flex items-center gap-2">
								<RadioGroupItem
									value="backup_code"
									id="add-order-backup-code"
								/>
								<Label htmlFor="add-order-backup-code">
									{t("addOrder.inputBackupCode")}
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<RadioGroupItem value="gauth" id="add-order-gauth" />
								<Label htmlFor="add-order-gauth">
									{t("addOrder.inputGauth")}
								</Label>
							</div>
						</RadioGroup>
						{authType === "gauth" && (
							<Typography variant="body-md" className="text-secondary-text">
								{t("addOrder.gauthHelp")}
							</Typography>
						)}
						{authType === "gauth" ? (
							<FormField
								control={form.control}
								name="gauth"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>{t("addOrder.inputGauth")}</FormLabel>
										<FormControl>
											<Input {...field} ltr autoComplete="off" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						) : (
							<ChipInput
								chips={form.watch("backup_code")}
								onChipsChange={(chips) =>
									form.setValue("backup_code", chips, { shouldValidate: true })
								}
								label={t("addOrder.inputBackupCode")}
								error={form.formState.errors.backup_code?.message}
							/>
						)}
					</fieldset>
				</fieldset>
			</form>
		</Form>
	);
};

export default AddOrderForm;
